/* eslint-disable array-bracket-newline */
import { useState, useEffect } from 'react';

interface IDebounceInputProps {
  className?: string;
  token?: string;
  onChange: (value: number) => void;
  onFocus?: () => void;
  default?: string;
  placeholder?: string;
  value: any;
  type?: string;
  min?: number;
  max?: number;
  step?: number;
  tip?: string;
  disabled?: boolean;
}

/**
 * Checks if values is in the bounds
 * If not, returns true and updated value
 * @param state - string input
 * @param min - minimum value
 * @param max - maximum value
 * @returns
 */
const useBounds = (state: string, min?: number, max?: number) => {
  let bounds = false;
  let value = Number(state);

  if (max && value > max) {
    value = max;
    bounds = true;
  }
  if (min && value < min) {
    value = min;
    bounds = true;
  }

  return {
    bounds,
    value,
  };
};

/**
 * Port of uniswap logic
 * @param string
 */
const escapeRegExp = (string: string): string =>
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

/**
 * match escaped "." characters via in a non-capturing group
 */
const inputRegex = RegExp('^\\d*(?:\\\\[.])?\\d*$');

/**
 * Locale-agnostic number formatter
 */
const numberFormatter = new Intl.NumberFormat('en-GB', {
  style: 'decimal',
  useGrouping: false,
  minimumFractionDigits: 0,
  maximumFractionDigits: 18,
});

const DebounceInput = (props: IDebounceInputProps) => {
  const [state, setState] = useState<string>(props.default || '0');
  const [controlled, setControlled] = useState<boolean>(false);

  /**
   * Gracefull exit edit mode
   * @returns
   */
  const onStopEdit = () => setTimeout(() => setControlled(false), 500);

  /**
   * Trigger only if field was edited by user
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (controlled) {
        const { bounds, value } = useBounds(state, props.min, props.max);
        if (bounds) {
          setState(String(value));
          props.onChange(value);
        } else {
          props.onChange(Math.abs(Number(state)));
        }
      }
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [state, controlled, props.min, props.max]);

  const enforcer = (nextUserInput: string) => {
    if (nextUserInput === '' || inputRegex.test(escapeRegExp(nextUserInput))) {
      setState(nextUserInput);
    }
  };

  const onInputChange = (e: any) => enforcer(e.target.value.replace(/,/g, '.'));

  // Handle props change
  useEffect(() => {
    // Handle NaN case
    if (Number.isNaN(props.value)) {
      setState('0');
      props.onChange(0);
    }

    const stringNumber = numberFormatter.format(props.value);

    let [int, dec] = stringNumber.split('.');
    if (dec && dec.length > 8) {
      dec = dec.substring(0, 8);
      setState(`${int}.${dec}`);
    } else {
      setState(stringNumber);
    }
  }, [props.value]);

  return (
    <input
      onChange={onInputChange}
      value={state}
      className={props.className}
      pattern='^[0-9]*[.,]?[0-9]*$'
      onFocus={() => setControlled(true)}
      onBlur={() => onStopEdit()}
      onClick={() => setControlled(true)}
      inputMode='decimal'
      autoComplete='off'
      autoCorrect='off'
      type='text'
      placeholder={props.placeholder || '0.0'}
      minLength={1}
      maxLength={21}
      data-tip={props.tip}
      spellCheck='false'
      disabled={props.disabled ?? false}
    />
  );
};

export default DebounceInput;
